/**
 * Reservation detail drawer (DNKADM-0032 U4; re-pointed to the owned v2 DTO in
 * DNKADM-0061 U1) — the right-side MUI `Drawer` opened from the calendar shell's
 * selected booking. Read-only detail + the two write actions.
 *
 * **DNKADM-0061 re-point.** The detail now renders the owned, atomic v2 reservation
 * directly:
 *  - a **status chip** from `src/domain/v2Enums.ts` (the §4.8 lowercase value →
 *    `reservationStatusLabel` + the mode-aware `reservationStatusChip` via the
 *    shared `chipSx`, DNKADM-0293);
 *  - **Staff** and **Resource ("bay")** rows from `assignedStaffName` /
 *    `assignedResourceName` **directly off the DTO** (null assignments render only
 *    as the DNKADM-0213 confirmed/not-past "Unassigned" affordance; the Any-Bay
 *    correction dance / `policy_chain_id` are superseded, §9);
 *  - **Price / Tax / Credits** via the 0049 money/credits helpers (`formatMoney` with
 *    the `useTenantCurrency()` code; `formatCreditAmount`) — OMITTED when absent;
 *  - **Time** rendered AS-IS from the ISO-8601-with-offset `start`/`end` (NOT
 *    reformatted to local tz — the handoff renders the stored offset verbatim);
 *  - the stable **Reservation ID** (`reservationUuid`).
 *
 * DNKADM-0257 adds the contact reach-out rows: `contactEmail` as a `mailto:` link and
 * `contactPhone` as a `tel:` link, each omitted when absent (`contactPhone` is a
 * pending dink-api field, parsed absent → null).
 *
 * DNKADM-0394 U3 adds the **Booked by** row — who placed the booking and when, from the
 * DNKAPI-0547 `bookedByActor` create-audit actor. Omitted entirely when that object is
 * null. It reads `bookedByActor`, never the older `bookedBy` string beside it.
 *
 * Presentation only — the drawer raises `onReschedule` / `onCancel` / `onReassign`
 * to the shell, which owns the wizard, the `CancelReservationDialog`, the
 * `ReassignAssignmentDialog`, and the refetch-after-write (confirm/reassign replace the
 * drawer's copy from the mutation's returned reservation, DNKADM-0257; a cancel closes
 * the drawer, DNKADM-0415). The Staff/Resource rows
 * carry a "change" pencil on a **confirmed, not-yet-started** booking (DNKADM-0211
 * U2) that raises `onReassign` — the drawer itself never calls the API.
 * Two blocks inside it do, each handing the result back to the shell to reflect: the
 * pending-charge money actions, and Check payment and Close out on the payment banner
 * (DNKADM-0408 U1/U2).
 * The colored service header keeps the existing `deriveServiceColor` (pure, on the
 * `serviceName` alias). Closing clears the shell's selection via `onClose`.
 *
 * DNKADM-0415: a reservation with no `reservationUuid` renders nothing, the same as
 * `null` — never a header, payment band or action buttons over an empty record.
 */
import { useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import { resolveCalendarMaps } from '@/theme/maps/calendar';
import {
  getReservation,
  resolveReservationPayment,
  type PaymentCloseOutResolution,
  type Reservation,
  type ReservationPaymentCloseOut,
} from '@/api/reservations';
import {
  isAwaitingConfirmation,
  reservationStatusLabel,
  type ReservationStatus,
} from '@/domain/v2Enums';
import { formatMoney } from '@/utils/v2Format';
import { MemberNameLink } from '@/components/MemberNameLink';
import { clock12, dateLabel, isoClockRangeAsIs } from '@/utils/clockTime';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import { useStaffTier } from '@/auth/authHooks';
import {
  bookedByLine,
  CHECK_PAYMENT_NOTHING_TO_CHECK,
  CHECK_PAYMENT_OUTCOME_TEXT,
  CHECK_PAYMENT_UNPAID_NO_TAKE_PAYMENT_TEXT,
  CLOSE_OUT_ALREADY_RESOLVED_TEXT,
  CLOSE_OUT_CANCELLED_NOTHING_OWED_TEXT,
  CLOSE_OUT_CANCELLED_PAID_REFUND_TEXT,
  CLOSE_OUT_FORBIDDEN_TEXT,
  CLOSE_OUT_MARKED_PAID_TEXT,
  CLOSE_OUT_MARKED_UNPAID_TEXT,
  closeOutOtherChargesText,
  deriveServiceColor,
  derivePaymentState,
  isPaymentNeedsReview,
  paymentAmountLabel,
  paymentHowLabel,
  paymentBannerText,
  PAYMENT_IN_PROGRESS_HELP,
} from './derive';
import { isPaymentNotStrandedRefusal, toCheckPaymentErrorMessage } from '../reservationsError';
import { ReservationPendingChargeBlock } from './ReservationPendingChargeBlock';
import { CloseOutPaymentDialog } from '../CloseOutPaymentDialog';
import { serviceWithCoach } from '../coachLabel';

/**
 * Render an ISO-8601-with-offset instant **as-is** — split into a date + a clock label
 * read off the wire string WITHOUT converting to the viewer's local timezone (the
 * handoff renders the stored offset verbatim). Returns null when the string can't be
 * split into the expected `YYYY-MM-DDTHH:mm…±hh:mm` parts.
 */
function isoParts(iso: string | null): { date: string; clock: string } | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return null;
  // The clock is 12-hour (DNKADM-0386 U2) through the shared formatter; still read off
  // the string as written — no offset conversion.
  return { date: `${m[1]}-${m[2]}-${m[3]}`, clock: clock12(Number(m[4]), Number(m[5])) };
}

/** The "1:00 PM – 2:00 PM" time-row value, rendered as-is from the offsets. Degrades. */
function timeRowValue(reservation: Reservation): string {
  const span = isoClockRangeAsIs(reservation.start, reservation.end);
  if (reservation.durationMinutes !== null && reservation.durationMinutes > 0) {
    return `${span}  (${reservation.durationMinutes} min)`;
  }
  return span;
}

/** The header date — the date portion of the ISO start, as-is (no tz shift), in words: `Thu, Sep 17, 2026`. */
function headerDate(reservation: Reservation): string | null {
  return dateLabel(isoParts(reservation.start)?.date ?? null);
}

/**
 * Best-effort "already started" test for the DNKADM-0211 reassign affordance. The
 * server's `409 RESERVATION_PAST` is the real backstop, so an absent/unparseable
 * start degrades to NOT past — the control stays offered and the server decides.
 */
function hasStarted(start: string | null): boolean {
  if (!start) return false;
  const t = Date.parse(start);
  return !Number.isNaN(t) && t <= Date.now();
}

interface ReservationDrawerProps {
  /** The selected booking, or null (drawer closed). */
  reservation: Reservation | null;
  /**
   * Whether this host lets the operator act on the booking. `false` for My schedule,
   * which mounts this drawer as a view (DNKADM-0363): the footer action grid, the
   * Staff/Resource reassign pencils and the pending-charge money actions are then
   * ABSENT — hidden, not disabled. Everything the READ carries still renders. Defaults
   * to `true`; a STAFF session on the board acts like any operator (DNKADM-0375).
   */
  canWrite?: boolean;
  /** Clear the shell's selection (backdrop / close button). */
  onClose: () => void;
  /** Open the reschedule wizard for this booking. */
  onReschedule: (reservation: Reservation) => void;
  /** Open the cancel dialog for this booking. */
  onCancel: (reservation: Reservation) => void;
  /** Raise the check-in (mark Showed) action to the shell (DNKADM-0146). */
  onCheckIn: (reservation: Reservation) => void;
  /** Raise the mark-No-show action to the shell (DNKADM-0146). */
  onNoShow: (reservation: Reservation) => void;
  /** Raise the confirm action for an unconfirmed (`new`) booking (DNKADM-0147). */
  onConfirm: (reservation: Reservation) => void;
  /** Open the staff/bay reassignment dialog for this booking (DNKADM-0211 U2). */
  onReassign: (reservation: Reservation) => void;
  /**
   * Open the pending request's Edit time dialog — `EditReservationDetailsDialog`,
   * the SAME dialog the Pending tab's "Edit time" opens (DNKADM-0393 U2).
   */
  onEditTime: (reservation: Reservation) => void;
  /**
   * Open the pending request's Change Resource/Staff dialog —
   * `ChangeAssignmentDialog` (`PUT …/details`), the SAME dialog the Pending tab
   * opens (DNKADM-0393 U2). Distinct from {@link onReassign}, which is the
   * DNKAPI-0356 reassign endpoint used on a CONFIRMED booking.
   */
  onChangeAssignment: (reservation: Reservation) => void;
  /** Refresh the selected reservation and loaded calendar after a pending-charge write. */
  onPendingChargeChanged: (reservationUuid: string) => Promise<void>;
  /**
   * Open Take payment for an UNPAID booking (DNKADM-0402 U5). Optional: a host that
   * cannot settle money (My schedule, which mounts this drawer read-only) simply omits
   * it and the action is absent — hidden, not disabled, like every other affordance
   * this drawer withholds. Never offered without `canWrite`.
   */
  onTakePayment?: (reservation: Reservation) => void;
  /**
   * Check payment (DNKADM-0408 U1) finished: the booking as the check left it, for the
   * host to replace its copy in place and refetch behind it — the same reflection Take
   * payment's success uses, without its toast (the outcome is said inline, under the
   * banner, because the desk acts on it). Optional: a host that omits it (My schedule)
   * gets no Check payment.
   *
   * Deliberately NOT gated on `canWrite`: dink-api opens `…/payment/resolve` to the STAFF
   * floor (`STAFF_ROLES`) and it never charges, so the floor may use it (the spec's "STAFF
   * may use it"). It is never offered on a cancelled booking.
   */
  onPaymentChecked?: (updated: Reservation) => void;
  /**
   * Close out (DNKADM-0408 U2) finished: the booking as the close-out left it, reflected
   * exactly as {@link onPaymentChecked} is. Optional, and the host passes it ONLY for an
   * admin session (`isAdmin`): dink-api opens `…/payment/close-out` to OWNER/ADMIN alone,
   * so the STAFF floor never sees the control. A host that omits it gets no Close out.
   *
   * Offered on a cancelled booking too: money taken for a cancelled booking is one of the
   * cases Close out exists for (DNKAPI-0565).
   */
  onPaymentClosedOut?: (updated: Reservation) => void;
}

export function ReservationDrawer({
  reservation: reservationProp,
  canWrite = true,
  onClose,
  onReschedule,
  onCancel,
  onCheckIn,
  onNoShow,
  onConfirm,
  onReassign,
  onEditTime,
  onChangeAssignment,
  onPendingChargeChanged,
  onTakePayment,
  onPaymentChecked,
  onPaymentClosedOut,
}: ReservationDrawerProps) {
  // DNKADM-0415: a record with no id is not a loaded reservation (a non-reservation
  // body parses to all nulls), so the drawer stays shut rather than showing an empty
  // shell with live actions and a payment band.
  const reservation =
    reservationProp !== null && reservationProp.reservationUuid !== null ? reservationProp : null;
  const theme = useTheme();
  // DNKADM-0408 U2: the other succeeded charges a close-out found, per booking. Held here,
  // not in the banner, so the notice survives the drawer closing and reopening on the same
  // booking: it stays until staff dismiss it.
  const [otherCharges, setOtherCharges] = useState<Record<string, string[]>>({});
  const cm = resolveCalendarMaps(theme);
  const mode = theme.palette.mode;
  const open = reservation !== null;
  const color = reservation ? deriveServiceColor(reservation.serviceName, mode) : null;
  // DNKADM-0375: dink-api opens `GET tenant-settings` to STAFF, so every operator reads
  // the currency (Price, Tax, the banner amount and Take payment need it).
  const { currency } = useTenantCurrency();
  // Editing or waiving a pending fee stays OWNER/ADMIN on the server (DNKAPI-0532); a
  // STAFF session may still Charge now. Render hint only.
  const canEditPendingFee = !useStaffTier();
  const status: ReservationStatus | null = reservation?.status ?? null;
  const cancelled = status === 'cancelled';
  // The reassign pencils (DNKADM-0211 U2) — offered ONLY on a confirmed booking that
  // has not started yet. Unconfirmed bookings keep the DNKADM-0200 `/details` Edit.
  const canReassign =
    canWrite && status === 'confirmed' && !hasStarted(reservation?.start ?? null);
  const selectedUuid = reservation?.reservationUuid ?? null;
  const otherChargeIds = selectedUuid !== null ? otherCharges[selectedUuid] : undefined;
  function dismissOtherCharges() {
    if (selectedUuid === null) return;
    setOtherCharges((prev) => {
      const next = { ...prev };
      delete next[selectedUuid];
      return next;
    });
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 380, maxWidth: '100vw' } } }}
    >
      {reservation && color && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Colored header */}
          <Box sx={{ bgcolor: color.main, color: cm.headerInk, px: 2.5, py: 2.25 }}>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  opacity: 0.9,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {reservation.serviceName ?? 'Reservation'}
              </Typography>
              <IconAction
                size="small"
                onClick={onClose}
                label="Close"
                sx={{ color: cm.headerInk, mt: -0.5, mr: -0.5 }}
              >
                <Icon name="close" sx={{ fontSize: 22 }} />
              </IconAction>
            </Stack>
            <Typography component="h2" sx={{ fontSize: 24, fontWeight: 500, mt: 0.75 }}>
              {reservation.contactName === null && reservation.contactId === null ? (
                (reservation.serviceName ?? '—')
              ) : (
                <MemberNameLink name={reservation.contactName} contactId={reservation.contactId} />
              )}
            </Typography>
            {reservation.assignedStaffName && (
              <Typography sx={{ fontSize: 14, opacity: 0.92, mt: 0.25 }}>
                {serviceWithCoach(reservation)}
              </Typography>
            )}
            {headerDate(reservation) && (
              <Typography sx={{ fontSize: 14, opacity: 0.92, mt: 0.25 }}>
                {headerDate(reservation)}
              </Typography>
            )}
            {/* Status: a dot and a word in the HEADER, not a button-lookalike
                in the body (DNKADM-0300 G26 — the state carries by word, so it
                is never color-only, and it can't read as a disabled action). */}
            {status && (
              <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                <Box
                  aria-hidden
                  sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cm.headerInk, opacity: 0.9 }}
                />
                <Typography sx={{ fontSize: 13, fontWeight: 600, opacity: 0.95 }}>
                  {reservationStatusLabel(status)}
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Payment banner (DNKADM-0350 U3) — the SAME four states + colors as
              the Day grid's block badge (U2), derived from the same pure
              functions so the two surfaces can never drift on the same
              reservation. Above the details table (a glance, not a read); the
              existing `Payment` detail row below stays for reading. */}
          {/* Keyed by the booking: a Check payment outcome belongs to the booking it was
              checked for and must not linger onto the next one opened. */}
          <PaymentBanner
            key={reservation.reservationUuid ?? ''}
            reservation={reservation}
            currency={currency}
            canWrite={canWrite}
            {...(canWrite && !cancelled && onTakePayment !== undefined
              ? { onTakePayment: () => onTakePayment(reservation) }
              : {})}
            {...(!cancelled && onPaymentChecked !== undefined ? { onPaymentChecked } : {})}
            {...(onPaymentClosedOut !== undefined
              ? {
                  onPaymentClosedOut: (updated: Reservation, others: string[]) => {
                    const uuid = updated.reservationUuid;
                    if (uuid && others.length > 0) {
                      setOtherCharges((prev) => ({ ...prev, [uuid]: others }));
                    }
                    onPaymentClosedOut(updated);
                  },
                }
              : {})}
          />
          {otherChargeIds !== undefined && (
            <Box sx={{ px: 2.5, pt: 1 }}>
              <Alert severity="warning" onClose={dismissOtherCharges}>
                {closeOutOtherChargesText(otherChargeIds)}
              </Alert>
            </Box>
          )}

          {/* Detail rows */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 1.5 }}>
            <DetailRow label="Time" value={timeRowValue(reservation)} />
            {/* Contact reach-out lines (DNKADM-0257): best-effort email/phone rendered
                as actionable mailto:/tel: links; an absent field omits its line
                (contactPhone is a pending dink-api addition — absent → null). */}
            {reservation.contactEmail && (
              <DetailRow
                label="Email"
                value={
                  <Link href={`mailto:${reservation.contactEmail}`} sx={{ wordBreak: 'break-all' }}>
                    {reservation.contactEmail}
                  </Link>
                }
              />
            )}
            {reservation.contactPhone && (
              <DetailRow
                label="Phone"
                value={<Link href={`tel:${reservation.contactPhone}`}>{reservation.contactPhone}</Link>}
              />
            )}
            {/* Assigned staff / resource — rendered DIRECTLY off the DTO. Assigned rows
                keep the DNKADM-0211 "change" pencil; a confirmed, not-yet-started null
                dimension renders "Unassigned" with an assign pencil (DNKADM-0213).
                Both raise the SAME `onReassign` (the shell's one dialog covers both
                dimensions). */}
            {reservation.assignedStaffName ? (
              <DetailRow
                label="Staff"
                value={reservation.assignedStaffName}
                action={
                  canReassign ? (
                    <ChangeAssignmentButton
                      label="Change staff"
                      onClick={() => onReassign(reservation)}
                    />
                  ) : null
                }
              />
            ) : canReassign ? (
              <DetailRow
                label="Staff"
                value="Unassigned"
                action={
                  <ChangeAssignmentButton
                    label="Assign staff"
                    onClick={() => onReassign(reservation)}
                  />
                }
              />
            ) : null}
            {reservation.assignedResourceName ? (
              <DetailRow
                label="Resource"
                value={reservation.assignedResourceName}
                action={
                  canReassign ? (
                    <ChangeAssignmentButton
                      label="Change bay"
                      onClick={() => onReassign(reservation)}
                    />
                  ) : null
                }
              />
            ) : canReassign ? (
              <DetailRow
                label="Resource"
                value="Unassigned"
                action={
                  <ChangeAssignmentButton
                    label="Assign bay"
                    onClick={() => onReassign(reservation)}
                  />
                }
              />
            ) : null}
            <DetailRow label="Service" value={reservation.serviceName ?? '—'} />
            {/* Money / credits via the 0049 helpers — omitted when absent. */}
            {reservation.priceCharged !== null && currency && (
              <DetailRow label="Price" value={formatMoney(reservation.priceCharged, currency)} />
            )}
            {reservation.taxCharged !== null && currency && (
              <DetailRow label="Tax" value={formatMoney(reservation.taxCharged, currency)} />
            )}
            {/* DNKADM-0454: how it was paid, never a "0.00" credits row on a card booking. */}
            {paymentHowLabel(reservation, currency) !== null && (
              <DetailRow label="Payment method" value={paymentHowLabel(reservation, currency) ?? ''} />
            )}
            {reservation.paymentStatus && (
              <DetailRow label="Payment" value={reservation.paymentStatus} />
            )}
            {reservation.pendingCharge && (
              <ReservationPendingChargeBlock
                reservation={reservation}
                canWrite={canWrite}
                canEditFee={canEditPendingFee}
                onChanged={onPendingChargeChanged}
              />
            )}
            {/* Who placed the booking (DNKADM-0394 U3) — the create-audit actor, in
                words: "Front desk: info@simcreek.com", "Member app: Sophia Craft",
                "Guest checkout", with the date and time underneath. The whole row is
                OMITTED when `bookedByActor` is null (no create-audit row): the shipped
                `bookedBy` string is NOT a substitute — it names the route but carries no
                identity, no timestamp and no session mode, so filling the row from it
                would say less while looking like it said more. */}
            <BookedByRow reservation={reservation} />
            <DetailRow
              label="Reservation ID"
              value={
                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, color: cm.textMeta }}>
                  {reservation.reservationUuid ?? '—'}
                </Box>
              }
              last
            />
          </Box>

          {/* Footer actions (DNKADM-0300 G): ONE shared grid separating
              routine (Confirm/Check-in primary + outlined Mark No-show /
              Reschedule) from destructive (Cancel as red TEXT below a divider
              — deliberately lighter than Reschedule). Money actions live in
              the pending-charge block beside the amount they act on. Every
              callback fires on the SAME conditions as before — presentation
              only.

              For a view-only host (`canWrite` false — My schedule) the whole
              footer goes, its divider with it — an empty rule under the last detail row would
              be a visible seam around nothing. The cancelled NOTE is not an
              action, so it stays: it is the read's own status, said in words
              where the actions would otherwise be. */}
          {cancelled ? (
            <>
              <Divider />
              <Box
                sx={{ px: 2.5, py: 1.75, textAlign: 'center', color: cm.textMuted, fontSize: 13 }}
              >
                This reservation has been cancelled
              </Box>
            </>
          ) : canWrite ? (
            <>
              <Divider />
              <FooterActions
                reservation={reservation}
                status={status}
                canReassign={canReassign}
                onConfirm={onConfirm}
                onCheckIn={onCheckIn}
                onNoShow={onNoShow}
                onReschedule={onReschedule}
                onCancel={onCancel}
                onReassign={onReassign}
                onEditTime={onEditTime}
                onChangeAssignment={onChangeAssignment}
              />
            </>
          ) : null}
        </Box>
      )}
    </Drawer>
  );
}

/**
 * The footer's ONE action grid (DNKADM-0300 G — replaces the DNKADM-0146
 * `AttendanceActions` row + the mixed `fullWidth`/`flex:'none'` footer that
 * could never align). Every callback fires on the SAME conditions as before:
 *  - `new` → filled **Confirm booking** (primary), outlined Check in (Showed)
 *    + Mark No-show, Reschedule.
 *  - `confirmed` → filled **Check in (Showed)** (primary), outlined Mark
 *    No-show + Reschedule.
 *  - `showed` → outlined Mark No-show (the attendance STATE is the header's
 *    dot-and-word, never a button-lookalike chip).
 *  - `no_show` → filled Check in (Showed) (the switch back).
 * Cancel booking sits below a divider as red TEXT — present for every
 * active status, but visibly lighter than Reschedule (G27).
 *
 * **DNKADM-0426 — showed / no_show offer neither Reschedule nor Cancel.**
 * dink-api treats both as terminal for cancel/reschedule (it answers 404
 * "Reservation not found"), so the desk was offered buttons that could only
 * fail. A guidance line takes their place.
 *
 * **DNKADM-0393 U2 — the pending booking can finally be acted on here.** The
 * grid's drawer showed a `unconfirmed` booking nothing but Reschedule and
 * Cancel, because every predicate above keyed on `'new'`: `primary` asked for
 * `status === 'new'`, and `canCheckIn`/`canNoShow` list only
 * confirmed/new/no_show/showed. `unconfirmed` — the status the Pending tab is
 * built on, labelled "Pending confirmation" — matched none of them and fell
 * through to the two status-agnostic controls. The drawer's `onConfirm` /
 * `onReassign` props were wired the whole time; nothing ever reached them.
 * So `unconfirmed` now offers the SAME four actions the Pending tab offers,
 * raised to the shell, which opens the SAME dialogs that tab opens:
 *  - **Confirm** (the filled primary) → `confirmReservation`,
 *  - **Edit time** → `EditReservationDetailsDialog`,
 *  - **Change coach/bay** → `ChangeAssignmentDialog` (`PUT …/details`),
 *  - **Decline request** → `CancelReservationDialog` (the Cancel control,
 *    carrying the Pending tab's word for the same dialog).
 * A **confirmed** booking additionally gets Change coach/bay as a footer button
 * → `ReassignAssignmentDialog` (the DNKAPI-0356 reassign, the same dialog the
 * Staff/Resource row pencils already open), on the same
 * confirmed-and-not-started gate as those pencils.
 */
function FooterActions({
  reservation,
  status,
  canReassign,
  onConfirm,
  onCheckIn,
  onNoShow,
  onReschedule,
  onCancel,
  onReassign,
  onEditTime,
  onChangeAssignment,
}: {
  reservation: Reservation;
  status: ReservationStatus | null;
  canReassign: boolean;
  onConfirm: (reservation: Reservation) => void;
  onCheckIn: (reservation: Reservation) => void;
  onNoShow: (reservation: Reservation) => void;
  onReschedule: (reservation: Reservation) => void;
  onCancel: (reservation: Reservation) => void;
  onReassign: (reservation: Reservation) => void;
  onEditTime: (reservation: Reservation) => void;
  onChangeAssignment: (reservation: Reservation) => void;
}) {
  // The pending (awaiting-operator-approval) booking — the Pending tab's whole
  // population, and the status this footer used to have no answer for.
  const pending = status === 'unconfirmed';
  const canCheckIn = status === 'confirmed' || status === 'new' || status === 'no_show';
  const canNoShow = status === 'confirmed' || status === 'new' || status === 'showed';
  // The one filled primary: Confirm on a booking awaiting confirmation, else Check in.
  const primary: 'confirm' | 'checkin' | null =
    isAwaitingConfirmation(status) ? 'confirm' : canCheckIn ? 'checkin' : null;
  const checkInSecondary = canCheckIn && primary !== 'checkin';
  // DNKADM-0426: showed / no_show are attendance outcomes, terminal for cancel and
  // reschedule on dink-api (it refuses both), so neither control is offered; a
  // guidance line says what to do instead.
  const attended = status === 'showed' || status === 'no_show';
  const hasWaivableCharge = reservation.pendingCharge?.status === 'pending';
  // Change coach/bay as a footer button on a confirmed, not-yet-started booking
  // (DNKADM-0393 U2) — the same dialog and the same gate as the row pencils.
  const showReassign = canReassign && !pending;
  // Reschedule pairs with an odd secondary, spans the row otherwise.
  const secondaryCount =
    (checkInSecondary ? 1 : 0) +
    (canNoShow ? 1 : 0) +
    (pending ? 2 : 0) +
    (showReassign ? 1 : 0);
  const rescheduleSpans = secondaryCount % 2 === 0;

  return (
    <Stack sx={{ gap: 1.25, px: 2.5, py: 1.75 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
        {primary === 'confirm' && (
          <Button
            variant="contained"
            color="success"
            startIcon={<Icon name="event_available" />}
            onClick={() => onConfirm(reservation)}
            sx={{ gridColumn: '1 / -1', minHeight: 48 }}
          >
            Confirm booking
          </Button>
        )}
        {primary === 'checkin' && (
          <Button
            variant="contained"
            color="success"
            startIcon={<Icon name="check_circle" />}
            onClick={() => onCheckIn(reservation)}
            sx={{ gridColumn: '1 / -1', minHeight: 48 }}
          >
            Check in (Showed)
          </Button>
        )}
        {checkInSecondary && (
          <Button
            variant="outlined"
            color="success"
            startIcon={<Icon name="check_circle" />}
            onClick={() => onCheckIn(reservation)}
            sx={{ minHeight: 48 }}
          >
            Check in (Showed)
          </Button>
        )}
        {canNoShow && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<Icon name="person_off" />}
            onClick={() => onNoShow(reservation)}
            sx={{ minHeight: 48, ...(attended ? { gridColumn: '1 / -1' } : {}) }}
          >
            Mark No-show
          </Button>
        )}
        {/* The pending request's own two edits (DNKADM-0393 U2) — the Pending
            tab's "Edit time" and "Change Resource/Staff", the same dialogs. */}
        {pending && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Icon name="schedule" />}
            onClick={() => onEditTime(reservation)}
            sx={{ minHeight: 48 }}
          >
            Edit time
          </Button>
        )}
        {pending && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Icon name="swap_horiz" />}
            onClick={() => onChangeAssignment(reservation)}
            sx={{ minHeight: 48 }}
          >
            Change coach/bay
          </Button>
        )}
        {/* Change coach/bay on a confirmed booking (DNKADM-0393 U2) — the
            DNKAPI-0356 reassign dialog the row pencils already open, given a
            footer button so it is findable without hunting the detail rows. */}
        {showReassign && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Icon name="swap_horiz" />}
            onClick={() => onReassign(reservation)}
            sx={{ minHeight: 48 }}
          >
            Change coach/bay
          </Button>
        )}
        {!attended && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Icon name="event_repeat" />}
            onClick={() => onReschedule(reservation)}
            sx={{ minHeight: 48, ...(rescheduleSpans ? { gridColumn: '1 / -1' } : {}) }}
          >
            Reschedule
          </Button>
        )}
      </Box>
      <Divider />
      {attended && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {status === 'showed'
            ? hasWaivableCharge
              ? 'Checked in. To not charge for this booking, use Waive on the pending charge.'
              : 'Checked in. A checked-in booking can’t be cancelled or rescheduled.'
            : 'Marked no-show. Switch back with Check in if they came.'}
        </Typography>
      )}
      {/* The same control and the same `CancelReservationDialog` for every
          status; a pending request simply carries the Pending tab's word for
          it — declining a request is not cancelling a booking (DNKADM-0393 U2). */}
      {!attended && (
        <Button
          variant="text"
          color="error"
          startIcon={<Icon name="cancel" />}
          onClick={() => onCancel(reservation)}
          sx={{ alignSelf: 'flex-start', minHeight: 44 }}
        >
          {pending ? 'Decline request' : 'Cancel booking'}
        </Button>
      )}
    </Stack>
  );
}

/**
 * The drawer's payment banner (DNKADM-0350 U3) — a full-width colored strip
 * carrying the same short text token as the grid badge, plus the amount where
 * there is one ("Paid · $60.00", "Due · $60.00 at check-in", or a bare
 * "Comp"). Rendered for every reservation (cancelled included — the money
 * state stays informative even once the slot is gone, unlike the Day-grid
 * block badge, which is corner real estate the cancelled treatment already
 * dims/strikes).
 *
 * DNKADM-0408 U1: on `in-progress` it carries **Check payment** where the host offers it,
 * in place of the "check it elsewhere" help. The outcome is said in words under the strip
 * and stays until the drawer moves to another booking — it is what the desk acts on.
 *
 * Check payment is offered only when the server says the payment is stranded
 * (`paymentStranded === true`, Operator decision): `in-progress` is derived from `pending` +
 * `amountDue`, and a pending booking the server does not call stranded has nothing for
 * `…/payment/resolve` to check, so it keeps the help line.
 *
 * DNKADM-0408 U2: on `needs-review` — or on `in-progress` once a Check payment answered
 * `needs_review` — it carries **Close out** where the host offers it (admin sessions only).
 */
function PaymentBanner({
  reservation,
  currency,
  canWrite,
  onTakePayment,
  onPaymentChecked,
  onPaymentClosedOut,
}: {
  reservation: Reservation;
  currency: string | null;
  /**
   * The drawer's `canWrite` — here it only picks the `unpaid` Check payment wording
   * (Operator decision, 2026-09-24): a session without Take payment is told to ask a
   * manager rather than to take payment again.
   */
  canWrite: boolean;
  /** Present only where the session may settle money on an UNPAID booking. */
  onTakePayment?: () => void;
  /** Present only where the host offers Check payment on a payment in progress. */
  onPaymentChecked?: (updated: Reservation) => void;
  /**
   * Present only where the host offers Close out (an admin session). Carries the booking
   * after the close-out and the other succeeded charges it found.
   */
  onPaymentClosedOut?: (updated: Reservation, otherSucceededPaymentIds: string[]) => void;
}) {
  const cm = resolveCalendarMaps(useTheme());
  const state = derivePaymentState(reservation);
  const pair = state === null ? null : cm.paymentState[state];
  const text =
    state === null ? '' : paymentBannerText(state, paymentAmountLabel(reservation, currency));
  // What the last Check payment said: info for an answer (the four outcomes and the 409),
  // error only for a call that failed outright.
  const [note, setNote] = useState<{ text: string; severity: 'info' | 'error' } | null>(null);
  const [checking, setChecking] = useState(false);
  // One call per click: the disabled button stops a second click once React re-renders;
  // this stops one that lands before it does.
  const inFlightRef = useRef(false);
  // DNKADM-0402 U5: the one action the Unpaid banner carries — taking the money for
  // THIS booking. It never appears on any other payment state — and deliberately NOT on
  // `in-progress`: that attempt's idempotency key is gone, so a charge from here could
  // take the money a second time (Operator decision, 2026-09-22).
  const takePayment = state === 'unpaid' && onTakePayment !== undefined;
  const inProgress = state === 'in-progress';
  // DNKADM-0408 U1: what `in-progress` offers instead — ask pay-api's ledger what
  // happened (DNKAPI-0563). It never charges.
  const checkPayment =
    inProgress && reservation.paymentStranded === true && onPaymentChecked !== undefined;
  // DNKADM-0408 U2: whether the last Check payment on this booking answered `needs_review`.
  const [checkedNeedsReview, setCheckedNeedsReview] = useState(false);
  const [closeOutOpen, setCloseOutOpen] = useState(false);
  // A `403` withdraws the control for this booking: the role check is the server's.
  const [closeOutForbidden, setCloseOutForbidden] = useState(false);
  const closeOut =
    onPaymentClosedOut !== undefined &&
    !closeOutForbidden &&
    (state === 'needs-review' || (inProgress && checkedNeedsReview));

  function closedOut(result: ReservationPaymentCloseOut, sent: PaymentCloseOutResolution) {
    setCloseOutOpen(false);
    const updated = result.reservation;
    const cancelledNow = updated.status === 'cancelled';
    const paid = (result.resolution ?? sent) === 'settle_to_payment';
    onPaymentClosedOut?.(updated, paid ? result.otherSucceededPaymentIds : []);
    setNote({
      text: paid
        ? cancelledNow
          ? `${CLOSE_OUT_MARKED_PAID_TEXT} ${CLOSE_OUT_CANCELLED_PAID_REFUND_TEXT}`
          : CLOSE_OUT_MARKED_PAID_TEXT
        : cancelledNow
          ? CLOSE_OUT_CANCELLED_NOTHING_OWED_TEXT
          : CLOSE_OUT_MARKED_UNPAID_TEXT,
      severity: 'info',
    });
  }

  async function closeOutAlreadyResolved() {
    setCloseOutOpen(false);
    setNote({ text: CLOSE_OUT_ALREADY_RESOLVED_TEXT, severity: 'info' });
    const uuid = reservation.reservationUuid;
    if (!uuid || !onPaymentClosedOut) return;
    try {
      onPaymentClosedOut(await getReservation(uuid), []);
    } catch {
      // The re-read is a courtesy; the line above is the answer.
    }
  }

  function closeOutRefused() {
    setCloseOutOpen(false);
    setCloseOutForbidden(true);
    setNote({ text: CLOSE_OUT_FORBIDDEN_TEXT, severity: 'error' });
  }

  async function check() {
    const uuid = reservation.reservationUuid;
    if (!uuid || !onPaymentChecked || inFlightRef.current) return;
    inFlightRef.current = true;
    setChecking(true);
    setNote(null);
    try {
      const { outcome, reservation: updated } = await resolveReservationPayment(uuid);
      setCheckedNeedsReview(outcome === 'needs_review');
      // A `needs_review` answer IS the review being raised: show it even if the read that
      // came back with it does not carry the flag yet.
      onPaymentChecked(
        outcome === 'needs_review' && !isPaymentNeedsReview(updated)
          ? { ...updated, paymentNeedsReview: true }
          : updated,
      );
      setNote(
        outcome === null
          ? null
          : {
              text:
                outcome === 'unpaid' && !canWrite
                  ? CHECK_PAYMENT_UNPAID_NO_TAKE_PAYMENT_TEXT
                  : CHECK_PAYMENT_OUTCOME_TEXT[outcome],
              severity: 'info',
            },
      );
    } catch (err) {
      if (isPaymentNotStrandedRefusal(err)) {
        // A normal answer, not an error: it was resolved elsewhere. Show what it is now.
        setNote({ text: CHECK_PAYMENT_NOTHING_TO_CHECK, severity: 'info' });
        try {
          onPaymentChecked(await getReservation(uuid));
        } catch {
          // The re-read is a courtesy; the line above is the answer.
        }
      } else {
        setNote({ text: toCheckPaymentErrorMessage(err), severity: 'error' });
      }
    } finally {
      inFlightRef.current = false;
      setChecking(false);
    }
  }

  // DNKADM-0437 V-20: no derivable payment state means no banner.
  if (pair === null) return null;
  return (
    <>
      <Stack
        sx={{
          px: 2.5,
          py: takePayment || checkPayment || closeOut ? 0.75 : 1,
          bgcolor: pair.bg,
          color: pair.fg,
          fontSize: 13,
          fontWeight: 600,
          gap: 0.25,
        }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Box>{text}</Box>
          {takePayment && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={onTakePayment}
              sx={{ flex: 'none' }}
            >
              Take payment
            </Button>
          )}
          {checkPayment && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => void check()}
              disabled={checking}
              sx={{ flex: 'none' }}
            >
              {checking ? 'Checking…' : 'Check payment'}
            </Button>
          )}
          {closeOut && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => setCloseOutOpen(true)}
              sx={{ flex: 'none' }}
            >
              Close out
            </Button>
          )}
        </Stack>
        {inProgress && !checkPayment && (
          <Box sx={{ fontSize: 12, fontWeight: 500 }}>{PAYMENT_IN_PROGRESS_HELP}</Box>
        )}
      </Stack>
      {note !== null && (
        <Box sx={{ px: 2.5, pt: 1 }}>
          <Alert severity={note.severity} {...(note.severity === 'info' ? { role: 'status' } : {})}>
            {note.text}
          </Alert>
        </Box>
      )}
      {closeOut && closeOutOpen && (
        <CloseOutPaymentDialog
          open
          reservation={reservation}
          onClose={() => setCloseOutOpen(false)}
          onClosedOut={closedOut}
          onAlreadyResolved={() => void closeOutAlreadyResolved()}
          onForbidden={closeOutRefused}
        />
      )}
    </>
  );
}

/**
 * The "Booked by" detail row (DNKADM-0394 U3) — renders nothing at all when the
 * reservation carries no create-audit actor, so the drawer shows one fewer row rather
 * than a row that says "—". The two lines come straight from the pure {@link bookedByLine}:
 * the route + identity on top, the date and time in words beneath it in the meta ink (the
 * same subordinate treatment the Reservation ID row uses). The timestamp is rendered as
 * written on the wire — the venue's clock, like `start`/`end` — never converted to the
 * viewer's local timezone.
 */
function BookedByRow({ reservation }: { reservation: Reservation }) {
  const cm = resolveCalendarMaps(useTheme());
  const line = bookedByLine(reservation.bookedByActor);
  if (line === null) return null;
  return (
    <DetailRow
      label="Booked by"
      value={
        <>
          <Box>{line.who}</Box>
          {line.when && (
            <Box sx={{ fontSize: 12, color: cm.textMeta, mt: 0.25 }}>{line.when}</Box>
          )}
        </>
      }
    />
  );
}

/** The row-level "change this assignment" pencil (DNKADM-0211 U2). */
function ChangeAssignmentButton({ label, onClick }: { label: string; onClick: () => void }) {
  const cm = resolveCalendarMaps(useTheme());
  return (
    <IconAction size="small" label={label} onClick={onClick} sx={{ color: cm.textMeta }}>
      <Icon name="edit" sx={{ fontSize: 18 }} />
    </IconAction>
  );
}

/** One label/value detail row (the design's `row()`), with an optional trailing action. */
function DetailRow({
  label,
  value,
  action = null,
  last = false,
}: {
  label: string;
  value: ReactNode;
  action?: ReactNode;
  last?: boolean;
}) {
  const cm = resolveCalendarMaps(useTheme());
  return (
    <Stack
      direction="row"
      sx={{
        py: '11px',
        borderBottom: last ? 'none' : `1px solid ${cm.rowBorder}`,
      }}
    >
      <Box sx={{ width: 110, flex: 'none', fontSize: 12, color: cm.textMuted, fontWeight: 500 }}>
        {label}
      </Box>
      <Box sx={{ fontSize: 14, color: cm.textPrimary }}>{value}</Box>
      {action && <Box sx={{ ml: 'auto', flex: 'none', alignSelf: 'center', my: '-6px' }}>{action}</Box>}
    </Stack>
  );
}
